import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seiteSperren, VERWERFEN } from '../../administration/components';
import { NurLesend } from '../../administration/readOnly';
import { EINSTELLUNGEN, NUR_LESEND_GRUND } from '../../config/ebenen';
import { TEXTE } from './Bausteine';
import { EinstLink, UngespeichertKontext, useRueckfrage, useSchliessen } from './EinstLink';
import { Fehlergrenze } from './Fehlergrenze';
import { dialogDarueber, tabImDialog, useFokusRueckgabe } from './fokus';
import { navSymbol } from './navSymbole';
import { EINSTELLUNGEN_PFAD, EinstellungsEintrag, EinstellungsGruppe, inSichtNurLesend, navAdresse, persoenlicheAdresse, SUCHE, sucheInGruppen } from './seiten';
import { useEinstellungsGruppen } from './useEinstellungsGruppen';
import '../../administration/administration.css';
import './einstellungen.css';

// Einstellungen als Pop-up über der aktuellen Ansicht (AE-1 Nachtrag 3, SPEC AE-01 bis AE-05): links die Bereiche,
// rechts der Inhalt im einen Gerüst (Bereich.tsx); die Recherche bleibt dahinter stehen. Schmal (unter 800 px)
// Vollbild: /einstellungen zeigt die Bereichsliste, ein Bereich den Weg „Alle Einstellungen“ zurück.

export const useGruppen = () => useOutletContext<EinstellungsGruppe[]>();

const SCHMAL = '(max-width: 799px)';
export function useSchmal() {
  const [schmal, setSchmal] = useState(() => window.matchMedia(SCHMAL).matches);
  useEffect(() => {
    const abfrage = window.matchMedia(SCHMAL);
    const wechsel = () => setSchmal(abfrage.matches);
    abfrage.addEventListener('change', wechsel);
    return () => abfrage.removeEventListener('change', wechsel);
  }, []);
  return schmal;
}

// Eine Gruppe ohne Titel („info“) steht ohne Beschriftung, durch eine Linie abgesetzt (SPEC AE-05); „Über …“ führt seit
// der Bewertung (e) nur noch das Profilmenü. Der offene Bereich
// trägt aria-current; am breiten Pop-up gilt /einstellungen als „Profil“. Nachbesserung B11: scheitert der Firmenkontext,
// nennt die Gruppe der Admin-Einstellungen das und bietet einen neuen Versuch (nochmals). Gestaltung 08.10.2026
// (GESTALTUNG.md 2.5): je Eintrag ein Symbol vor dem Titel; das Suchfeld darüber filtert die Einträge nach dem Titel,
// Gruppen ohne Treffer fallen weg (die Firmengruppe mit Fehlerhinweis bleibt).
// Plan 1a Welle 1, G3-3 (D-14): ein Treffer über ein Stichwort sagt unter dem Titel klein, was gefunden wurde.
// Plan 1a Welle 2, Spur admin (GZ-1; Ä-01 Punkt 1, Ä-18 Punkt 1): ein Unter-Eintrag („Besitz und Nachfolge“, „Sicherheit der
// Firma“) steht ohne Symbol und eingerückt unter „Firmendaten“ (einst-link--unter); markiert ist genau der Eintrag, dessen
// Pfad und Anker zur Adresse passen (navAdresse).
function eintragsInhalt(eintrag: EinstellungsEintrag) {
  const Symbol = eintrag.unter ? null : navSymbol(eintrag.key);
  return <>{Symbol && <Symbol size={16} aria-hidden="true" className="einst-link-symbol" />}<span className="einst-link-text">{eintrag.titel}
    {eintrag.gefunden && <span className="einst-link-fund">{SUCHE.gefunden(eintrag.gefunden)}</span>}</span></>;
}

function Navigation({ gruppen, aktuell, suche, nochmals, laden }: { gruppen: EinstellungsGruppe[]; aktuell: string; suche: string; nochmals?: () => void; laden?: boolean }) {
  const muster = suche.trim();
  // Plan 1a Welle 1, G3-3 (D-14, W1-09): Titel und Stichwörter (seiten.ts sucheInGruppen), ohne Groß-/Kleinschreibung und
  // ohne Umlautunterschied; ohne Treffer der Weg in die Hilfe.
  const sichtbar = sucheInGruppen(gruppen, suche)
    .filter(gruppe => !muster || gruppe.eintraege.length > 0 || (gruppe.key === 'firma' && !!nochmals));
  const keine = !!muster && !sichtbar.some(gruppe => gruppe.eintraege.length > 0);
  return <>
    <nav aria-label={EINSTELLUNGEN.titel} className="einst-nav">
      {sichtbar.map(gruppe => <div key={gruppe.key} className={gruppe.titel ? undefined : 'einst-nav-info'}>
        {gruppe.titel && <p className="einst-nav-titel" id={`einst-nav-${gruppe.key}`}>{gruppe.titel}</p>}
        {gruppe.untertitel && <p className="einst-nav-untertitel">{gruppe.untertitel}</p>}
        {gruppe.key === 'firma' && nochmals && <div className="einst-nav-fehler" role="alert">
          <p>{EINSTELLUNGEN.bereicheFehlen}</p>
          <button type="button" className="einst-link" onClick={nochmals}>{EINSTELLUNGEN.erneutVersuchen}</button>
        </div>}
        {gruppe.key === 'firma' && laden && !muster && <p className="einst-nav-leer" role="status" aria-busy="true" data-laden>{EINSTELLUNGEN.bereicheLaden}</p>}
        <ul aria-labelledby={gruppe.titel ? `einst-nav-${gruppe.key}` : undefined}>{gruppe.eintraege.map(eintrag => <li key={eintrag.key}>
          {eintrag.extern
            ? <Link to={eintrag.to} className="einst-link">{eintragsInhalt(eintrag)}</Link>
            : <EinstLink to={eintrag.to} className={eintrag.unter ? 'einst-link einst-link--unter' : 'einst-link'} aria-current={eintrag.to === aktuell ? 'page' : undefined}>{eintragsInhalt(eintrag)}</EinstLink>}
        </li>)}</ul>
      </div>)}
    </nav>
    {/* Die Meldung steht immer im DOM, damit Bildschirmleser den Wechsel hören; leer nimmt sie keinen Platz ein. */}
    <p className="einst-nav-leer" role="status">{keine ? TEXTE.navSucheLeer : ''}</p>
    {keine && <EinstLink to={SUCHE.hilfeZiel} className="einst-link einst-nav-hilfe">{SUCHE.hilfe}</EinstLink>}
  </>;
}

export default function Einstellungen() {
  const { user } = useAuth();
  const location = useLocation();
  const schmal = useSchmal();
  // Die Gruppen mit dem Firmenkontext (zuletzt geladener Stand je Firma, Laden, Fehler und neuer Versuch) berechnet derselbe
  // Hook wie auf der Konto-Seite (PF-L1): eine Berechnung, ein LETZTER_KONTEXT für Pop-up und Seite.
  const { gruppen, bereicheLaden, fehler, nochmals, firmenKontext } = useEinstellungsGruppen();
  const pfad = location.pathname.replace(/\/+$/, '');
  const liste = pfad === EINSTELLUNGEN_PFAD;
  // GZ-1: die Ansicht der Adresse – der Pfad, bei „Firmendaten“ mit dem Anker der Ansicht (…#besitz, …#sicherheit-der-firma).
  const ansicht = navAdresse(pfad, location.hash);
  const aktuell = liste && !schmal ? persoenlicheAdresse('profil') : ansicht;
  const dialog = useRef<HTMLDivElement>(null);
  const spalte = useRef<HTMLElement>(null);
  const inhalt = useRef<HTMLDivElement>(null);
  const [oben, setOben] = useState(0);
  const [suche, setSuche] = useState('');
  useFokusRueckgabe(dialog);

  // AE-02: im Programm geöffnet führt Schließen im Verlauf zurück, sonst ersetzt die Startansicht die Adresse.
  const { schliessen: sofortSchliessen } = useSchliessen();
  // Plan 1a Welle 2 (GB-06, G-12): hat ein Bereich ungespeicherte Eingaben (useUngespeichert), fragt der Rahmen vor einem
  // Bereichswechsel (EinstLink) und vor dem Schließen (Schließen-Knopf, Escape ohne Dialog darüber, Klick neben das Pop-up)
  // mit den Wörtern aus VERWERFEN. Ein Wechsel der Adresse auf anderem Weg (Browser-Zurück) nimmt die Frage wieder weg.
  const rueckfrage = useRueckfrage();
  const schliessen = () => { if (!rueckfrage.wache.fragen(sofortSchliessen)) sofortSchliessen(); };
  useEffect(() => rueckfrage.zuruecksetzen(), [pfad, rueckfrage.zuruecksetzen]);

  // AE-15: das Band einer Sicht bleibt über dem Pop-up sichtbar und bedienbar; der Hintergrund scrollt nicht.
  const sicht = user?.sicht?.aktiv ? `${user.sicht.aktiv.linse}:${user.sicht.aktiv.firmaName}` : '';
  useLayoutEffect(() => {
    const messen = () => { const band = document.querySelector('.sicht-band'); setOben(band ? Math.max(0, Math.round(band.getBoundingClientRect().bottom)) : 0); };
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, [sicht]);
  // DR-13 (Overlays NOTE 1): dieselbe zählende Sperre wie die Dialoge (components.tsx), sonst schnitt der body-Stil eines
  // Dialogs im Pop-up unter der Sperre am html-Element den Hintergrund ab.
  useLayoutEffect(() => seiteSperren(), []);

  // AE-03: beim Öffnen und nach einem Wechsel aus der schmalen Liste auf die Überschrift; ein Wechsel über die breite
  // Navigation lässt den Fokus auf dem Link. Ging er mit dem vorigen Bereich verloren (Link im Inhalt, Nachbesserung B7),
  // setzt ihn der Wechsel ebenfalls auf die Überschrift. Die Bereiche laden nach; bis dahin liegt der Fokus auf dem
  // Dialog. Beim Nachladen hält React den vorigen Bereich versteckt im DOM; gezählt wird deshalb nur eine sichtbare Überschrift.
  // Plan 1a Welle 2 (Ä-01, „Seitenanfang“): der Wechsel zwischen „Firmendaten“, „Besitz und Nachfolge“ und „Sicherheit der
  // Firma“ ändert nur den Anker und gilt hier wie ein Bereichswechsel (ansicht statt pfad).
  const erster = useRef(true);
  useEffect(() => {
    const zuerst = erster.current;
    erster.current = false;
    const aktiv = document.activeElement;
    const verloren = !aktiv || !dialog.current?.contains(aktiv) || aktiv.getClientRects().length === 0;
    if (!zuerst && !schmal && !verloren) return;
    if (zuerst || verloren) dialog.current?.focus();
    let versuche = 0, aus = false;
    const setzen = () => {
      if (aus) return;
      const ziel = [...(dialog.current?.querySelectorAll<HTMLElement>(liste && schmal ? '#einst-titel' : '.einst-inhalt .einst-kopf h1') ?? [])].find(el => el.getClientRects().length > 0);
      if (ziel) ziel.focus(); else if (versuche++ < 120) requestAnimationFrame(setzen);
    };
    setzen();
    return () => { aus = true; };
  }, [ansicht]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gestaltung 08.10.2026 (GESTALTUNG.md 2.2, 2.5): ein Seitenwechsel beginnt oben im Inhalt; beim Öffnen, nach jedem
  // Wechsel und sobald die Bereiche der Organisation geladen sind, rollt die Navigation den offenen Eintrag in Sicht,
  // ohne den Fokus zu verschieben (Meine Einstellungen stehen oben, ein Weg in die Admin-Einstellungen landet darunter).
  useEffect(() => { if (inhalt.current) inhalt.current.scrollTop = 0; }, [ansicht]);
  useEffect(() => {
    spalte.current?.querySelector<HTMLElement>('.einst-nav [aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [aktuell, firmenKontext]);

  // Suchfeld der Navigation: Enter öffnet den ersten Treffer über seinen Link (EinstLink behält den Hintergrund);
  // Escape leert zuerst den Text und lässt das Pop-up offen, mit leerem Feld schließt es wie überall im Pop-up.
  const sucheTasten = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && suche) { event.preventDefault(); event.stopPropagation(); setSuche(''); }
    else if (event.key === 'Enter') { event.preventDefault(); spalte.current?.querySelector<HTMLAnchorElement>('.einst-nav a.einst-link')?.click(); }
  };

  // Paket 08 Schritt 6 (F-08-6, VE-23): in einer Sicht liegen die persönlichen Bereiche hinter der Lesegrenze - jeder Knopf
  // bleibt sichtbar, gesperrt mit Grund; Bedienelemente mit data-lesend (Abmelden, VE-21) bleiben frei.
  const aktiveSicht = user?.sicht?.aktiv;
  // DR-13: ein Fehler beim Zeichnen eines Bereichs bleibt im Inhalt des Pop-ups (Fehlergrenze.tsx).
  const bereich = <Fehlergrenze adresse={ansicht}><Outlet context={gruppen} /></Fehlergrenze>;

  // AE-02, AE-16: Escape schließt nur, wenn kein Dialog über dem Pop-up offen ist; Tab bleibt im Pop-up.
  const tasten = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (dialogDarueber()) return;
      event.stopPropagation(); event.preventDefault(); schliessen();
    } else if (dialog.current) tabImDialog(event, dialog.current);
  };

  return createPortal(<div className="einst-overlay" style={{ top: oben }} onMouseDown={event => { if (event.target === event.currentTarget) schliessen(); }}>
    <UngespeichertKontext.Provider value={rueckfrage.wache}>
    <div ref={dialog} tabIndex={-1} className={`einst-dialog administration${liste ? ' einst-dialog--liste' : ''}`} role="dialog" aria-modal="true" aria-labelledby="einst-titel" onKeyDown={tasten}>
      <div className="einst-kopfleiste">
        {!liste && <EinstLink className="einst-zurueck" to={EINSTELLUNGEN_PFAD}><ChevronLeft size={18} aria-hidden="true" />{EINSTELLUNGEN.alle}</EinstLink>}
        <p id="einst-titel" tabIndex={-1}>{EINSTELLUNGEN.titel}</p>
        <button type="button" className="einst-schliessen" aria-label={EINSTELLUNGEN.schliessen} onClick={schliessen}><X size={18} aria-hidden="true" /></button>
      </div>
      <aside className="einst-spalte" ref={spalte}>
        <div className="einst-nav-suche"><Search size={16} aria-hidden="true" />
          <input type="search" aria-label={TEXTE.navSuche} placeholder={TEXTE.navSuchePlatzhalter} value={suche} autoComplete="off" spellCheck={false}
            onChange={event => setSuche(event.target.value)} onKeyDown={sucheTasten} /></div>
        <Navigation gruppen={gruppen} aktuell={aktuell} suche={suche} nochmals={fehler ? nochmals : undefined} laden={bereicheLaden} />
      </aside>
      <div className="einst-inhalt" ref={inhalt}>{aktiveSicht && inSichtNurLesend(pfad) ? <NurLesend grund={NUR_LESEND_GRUND(aktiveSicht.linse)}>{bereich}</NurLesend> : bereich}</div>
      {/* GB-06: die Rückfrage in derselben Form wie im Dialog (components.tsx Dialog), als Leiste am unteren Rand des Pop-ups. */}
      {rueckfrage.frage && <p className="adm-notice adm-verwerfen einst-verwerfen" role="alert" data-vor={VERWERFEN.verwerfen}>{VERWERFEN.frage} <button ref={rueckfrage.weiterKnopf} type="button" className="adm-button" onClick={rueckfrage.weiterBearbeiten}>{VERWERFEN.weiter}</button>
        <button type="button" className="adm-button adm-danger" onClick={rueckfrage.verwerfen}>{VERWERFEN.verwerfen}</button></p>}
    </div>
    </UngespeichertKontext.Provider>
  </div>, document.body);
}
